


import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import "highcharts/highcharts-more";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";

// Shimmer helpers (same pattern as Highchart & AgeCompare)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const AgingpaceSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  const isSmallMobile = window.innerWidth < 480;

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "55%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Gauge skeleton container */}
      <div
        style={{
          marginTop: 16,
          paddingInline: 16,
          paddingBottom: 24,
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: isSmallMobile ? 220 : 260,
            borderRadius: 18,
          }}
        />
      </div>
    </div>
  );
};

const Agingpace = ({ shareConfig } = {}) => {
  const [chartData, setChartData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    lifestyleApi
      .agingpaceData(encryptedId)
      .then((res) => {
        if (res?.data?.code === 200) {
          setChartData(res.data.data);
        }
      })
      .catch((err) => console.error(err));
  }, []);


  if (!chartData) return <AgingpaceSkeleton />;

  const { theme } = getUrlParams();
  const isDark = theme === "dark" || document.documentElement.classList.contains("dark");

  const isSmallMobile = windowWidth < 480;

  const options = {
    chart: {
      type: "gauge",
      height: isSmallMobile ? "160px" : "280px",
      backgroundColor: "transparent",
      style: { fontFamily: "Open Sans, sans-serif" },
    },

    title: { text: null },

    pane: {
      startAngle: -90,
      endAngle: 90,
      background: {
        backgroundColor: "var(--bg-surface)",
        innerRadius: isSmallMobile ? "88%" : "90%",
        outerRadius: "100%",
        shape: "arc",
      },
      center: ["50%", isSmallMobile ? "60%" : "65%"],
      size: isSmallMobile ? "100%" : "110%",
    },

    // ⭐ FIXED TOOLTIP — BLUE DOT + BLUE BORDER
    tooltip: {
      enabled: false,
      shared: false,
      followPointer: false,
      followTouchMove: false,

      formatter() {
        if (this.point && this.series?.name === "Pace of Aging") {
          return `
                        <span style="color:${this.point.color}">&#9679;</span>
                        Pace of Aging: ${this.point.y}
                    `;
        }
        return false;
      },

      borderRadius: 6,
      borderColor: "#2f7ed8",
      backgroundColor: isDark ? "#1e2a22" : "#ffffff",
      hideDelay: 200,
      stickOnContact: false,
      style: {
        fontFamily: "'Open Sans Regular', sans-serif",
        fontSize: "12px",
        color: isDark ? "#e0e0e0" : "#333333",
      },
    },

    yAxis: {
      min: 0.5,
      max: 1.7,
      tickPositions: [
        0.5, 0.6, 0.7, 0.8, 0.9,
        1.0, 1.1, 1.2, 1.3, 1.4,
        1.5, 1.6, 1.7,
      ],
      lineWidth: 0,
      minorTicks: false,
      tickLength: isSmallMobile ? 15 : 20,
      tickWidth: isSmallMobile ? 2 : 3,
      tickColor: "#f7f1f1",
      labels: {
        distance: isSmallMobile ? 15 : 15,
        style: {
          fontSize: "10px",
          color: "var(--text-primary)",
          fontFamily: "Open Sans, sans-serif",
        },
      },

      plotBands: [
        { from: 0.5, to: 0.9, color: "#55BF3B", thickness: isSmallMobile ? 15 : 20 },
        { from: 0.9, to: 1.1, color: "#DDDF0D", thickness: isSmallMobile ? 15 : 20 },
        { from: 1.1, to: 1.7, color: "#DF5353", thickness: isSmallMobile ? 15 : 20 },
      ],
    },

    plotOptions: {
      gauge: {
        dial: {
          radius: isSmallMobile ? "70%" : "80%",
          backgroundColor: "var(--text-muted)",
          baseWidth: isSmallMobile ? 9 : 10,
          topWidth: 2,
          baseLength: "0%",
          rearLength: "0%",
        },
        pivot: {
          radius: isSmallMobile ? 7 : 7,
          backgroundColor: "#646161",
        },
      },
    },

    series: [
      {
        name: "Pace of Aging",
        color: "#2f7ed8", // gauge series color
        data: [
          {
            y: chartData.pace,
            color: "#2f7ed8",
            marker: {
              enabled: true,
              radius: 5,
              fillColor: "#2f7ed8",
            },
          },
        ],

        dataLabels: {
          enabled: true,
          useHTML: true,
          borderWidth: 0,
          y: 12,

          format: `
                        <div style="text-align:center; font-family:${"Open Sans, sans-serif"}">
                            <div style="
                                font-size:14px;
                                font-weight:bold;
                                color:var(--text-primary);
                                font-family:${"Open Sans, sans-serif"};
                                margin-bottom:2px;
                            ">
                                ${chartData.pace}
                            </div>
                            <div style="
                                font-size:11px;
                                color:var(--text-secondary);
                                font-family:${"Open Sans, sans-serif"};
                            ">
                                ${chartData.subText}
                            </div>
                        </div>
                    `,
        },
      },
    ],

    credits: { enabled: false },
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/74917_1764052464.png"
        title={chartData.title}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        subtitle={chartData.heading}
        showInfo
        onInfoClick={() => {
          navigateWithFai("Pace of Aging", chartData.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default Agingpace;
